import { IsoWeek } from '../date/iso-week';
import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import type { ProjectId, WeeklyPlanId } from '../shared/ids';
import { PLANNING_ERROR, PLANNING_ERROR_MESSAGE } from './errors';

/**
 * Plano da semana.
 *
 * A semana ISO é a unidade de planejamento (RN-080): começa na segunda, termina no
 * domingo, e a virada do ano não a parte ao meio. O plano é o **contêiner** da semana —
 * quem carrega horas é a alocação.
 *
 * O plano existe mesmo vazio, de propósito: é ele que guarda a situação da semana
 * (rascunho, publicada, fechada) e serve de âncora para o histórico. Sem ele, "esta
 * semana está fechada" não teria onde morar.
 */
export type WeeklyPlanStatus = 'DRAFT' | 'PUBLISHED' | 'CLOSED';

export const WEEKLY_PLAN_STATUS_LABEL: Readonly<Record<WeeklyPlanStatus, string>> = {
  DRAFT: 'Rascunho',
  PUBLISHED: 'Publicada',
  CLOSED: 'Fechada',
};

/**
 * Transições permitidas.
 *
 * Fechada volta para publicada porque erro de fechamento acontece — e a alternativa
 * seria alguém "corrigir" o passado criando uma semana paralela. Reabrir é auditado
 * (RN-087); apagar o histórico, não.
 */
const ALLOWED_TRANSITIONS: Readonly<Record<WeeklyPlanStatus, readonly WeeklyPlanStatus[]>> = {
  DRAFT: ['PUBLISHED', 'CLOSED'],
  PUBLISHED: ['CLOSED', 'DRAFT'],
  CLOSED: ['PUBLISHED'],
};

export interface WeeklyPlan {
  readonly id: WeeklyPlanId;
  readonly projectId: ProjectId;
  readonly isoYear: number;
  readonly isoWeek: number;
  /** Segunda-feira, `"YYYY-MM-DD"` — desnormalizada para o índice de período. */
  readonly startDate: string;
  /** Domingo. */
  readonly endDate: string;
  readonly status: WeeklyPlanStatus;
  readonly publishedAt?: string;
  readonly closedAt?: string;
  readonly notes?: string;
  /** Bloqueio otimista — ver `weekly-allocation.ts`. */
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface CreateWeeklyPlanInput {
  readonly id: WeeklyPlanId;
  readonly projectId: ProjectId;
  readonly week: IsoWeek;
  readonly now: string;
}

export function createWeeklyPlan(input: CreateWeeklyPlanInput): WeeklyPlan {
  return {
    id: input.id,
    projectId: input.projectId,
    isoYear: input.week.isoYear,
    isoWeek: input.week.isoWeek,
    startDate: input.week.startDate().toString(),
    endDate: input.week.endDate().toString(),
    status: 'DRAFT',
    version: 1,
    createdAt: input.now,
    updatedAt: input.now,
  };
}

/** Chave natural da semana na obra (RN-080). */
export function weeklyPlanNaturalKey(
  projectId: ProjectId,
  isoYear: number,
  isoWeek: number,
): string {
  return `${projectId}|${String(isoYear)}|${String(isoWeek).padStart(2, '0')}`;
}

/** A semana ISO do plano, para navegar e formatar sem reconstruir datas na tela. */
export function weekOf(plan: WeeklyPlan): IsoWeek {
  return IsoWeek.ofParts(plan.isoYear, plan.isoWeek);
}

/**
 * Semana fechada é somente leitura (RN-087).
 *
 * A verificação vive aqui — e não em cada caso de uso — porque criar, alterar, excluir e
 * copiar precisam da mesma resposta. Um `if` repetido em quatro lugares é um `if`
 * esquecido no quinto.
 */
export function isEditable(plan: WeeklyPlan): boolean {
  return plan.status !== 'CLOSED';
}

export function ensureEditable(plan: WeeklyPlan): Result<void, DomainError> {
  if (isEditable(plan)) return ok(undefined);
  return err(
    domainError(PLANNING_ERROR.PLAN_CLOSED, PLANNING_ERROR_MESSAGE[PLANNING_ERROR.PLAN_CLOSED]!, {
      planId: plan.id,
      isoYear: plan.isoYear,
      isoWeek: plan.isoWeek,
    }),
  );
}

export function changeWeeklyPlanStatus(
  plan: WeeklyPlan,
  next: WeeklyPlanStatus,
  now: string,
): Result<WeeklyPlan, DomainError> {
  if (plan.status === next) return ok(plan);
  if (!ALLOWED_TRANSITIONS[plan.status].includes(next)) {
    return err(
      domainError(
        PLANNING_ERROR.PLAN_INVALID_TRANSITION,
        `Não é possível passar a semana de "${WEEKLY_PLAN_STATUS_LABEL[plan.status]}" para "${WEEKLY_PLAN_STATUS_LABEL[next]}".`,
        { from: plan.status, to: next },
      ),
    );
  }

  // Os carimbos são reescritos a cada entrada no estado: reabrir e fechar de novo tem de
  // registrar o fechamento novo, não o antigo.
  const { publishedAt: _published, closedAt: _closed, ...rest } = plan;

  return ok({
    ...rest,
    status: next,
    version: plan.version + 1,
    updatedAt: now,
    ...(next === 'PUBLISHED' ? { publishedAt: now } : {}),
    ...(next === 'CLOSED' ? { publishedAt: plan.publishedAt ?? now, closedAt: now } : {}),
  });
}

export function compareWeeklyPlans(a: WeeklyPlan, b: WeeklyPlan): number {
  return a.isoYear - b.isoYear || a.isoWeek - b.isoWeek;
}
